import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CalendarX, AlertTriangle } from 'lucide-react';
import { apiService } from '../services/api';
import { Event } from '../types';
import { ShareButton, ArticleContent, PageHeader } from '../components';

export const EventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getEvent(parseInt(id, 10));
        setEvent(data);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Événement non trouvé');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-church-light flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-church-brown mx-auto mb-4'></div>
          <p className='text-gray-600'>Chargement de l'événement...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center bg-white rounded-2xl shadow-xl border border-gray-200 p-12 max-w-md mx-4'>
          <CalendarX className='w-16 h-16 mx-auto mb-4 text-gray-300' />
          <h2 className='text-2xl font-semibold text-gray-900 mb-4 font-sans'>
            Événement non trouvé
          </h2>
          <p className='text-gray-600 mb-6 font-sans'>{error || "Cet événement n'existe pas."}</p>
          <button
            onClick={() => navigate('/')}
            className='inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-church-brown to-church-dark text-white rounded-lg hover:from-church-dark hover:to-church-brown transition-all duration-200 font-medium shadow-md hover:shadow-lg font-sans'
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' />
            </svg>
            Retour aux événements
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen'>
      {/* Preview Badge for non-published events */}
      {event.status !== 'published' && (
        <div className='bg-yellow-500 text-black px-4 py-3 text-center font-semibold shadow-lg sticky top-0 z-50 flex items-center justify-center gap-2'>
          <AlertTriangle className='w-5 h-5 shrink-0' />
          MODE PRÉVISUALISATION - Cet événement n'est pas encore publié (statut: {event.status})
        </div>
      )}
      
      {/* Page Title */}
      <PageHeader title={event.title} />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'>
        <div className='bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden'>
          <div className='p-8 md:p-12'>
            <div className='flex flex-col md:flex-row gap-8 lg:gap-12'>
              {/* Event Image - Left Side */}
              {event.image_path && (
                <div className='md:w-2/5 lg:w-1/3 flex-shrink-0'>
                  <div className='rounded-xl overflow-hidden shadow-lg sticky top-24'>
                    <img
                      src={apiService.getEventImageUrl(event.id)}
                      alt={event.title}
                      className='w-full h-auto object-cover'
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Content - Right Side */}
              <div className={`flex-1 ${event.image_path ? 'md:w-3/5 lg:w-2/3' : 'w-full'}`}>
                {/* Event Header */}
                <div className='mb-8 pb-6 border-b border-gray-200'>
                  <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6'>
                    <div className='space-y-3'>
                      <div className='px-5 py-3 bg-gradient-to-r from-church-gold to-church-brown rounded-lg inline-block'>
                        <div className='text-white font-semibold text-lg font-sans'>
                          {formatDate(event.event_date)}
                        </div>
                        <div className='text-white/90 text-base font-sans'>
                          {formatTime(event.event_date)}
                        </div>
                      </div>
                      {event.location && (
                        <div className='flex items-center gap-2 text-gray-700 text-lg font-sans'>
                          <svg className='w-5 h-5 text-church-gold' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' />
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 11a3 3 0 11-6 0 3 3 0 016 0z' />
                          </svg>
                          <span className='font-medium'>{event.location}</span>
                        </div>
                      )}
                    </div>
                    <ShareButton url={`${window.location.origin}/events/${event.id}`} />
                  </div>
                </div>

                {/* Event Details */}
                <div className='mb-8 space-y-6'>
                  {/* Description */}
                  <div className='prose prose-lg max-w-none'>
                    <ArticleContent
                      content={event.description}
                      className='text-gray-800 leading-relaxed font-sans'
                    />
                  </div>

                  {/* Additional Info */}
                  {event.registration_deadline && (
                    <div className='p-5 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200 shadow-sm'>
                      <div className='flex items-center gap-2 mb-2'>
                        <svg className='w-5 h-5 text-orange-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                        </svg>
                        <span className='font-semibold text-orange-900 font-sans'>Date limite d'inscription</span>
                      </div>
                      <p className='text-orange-800 font-sans'>
                        {formatDate(event.registration_deadline)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Files Section */}
                {event.files && event.files.length > 0 && (
                  <div className='mb-8 pt-6 border-t border-gray-200'>
                    <h3 className='text-xl font-semibold text-gray-900 mb-4 font-sans flex items-center gap-2'>
                      <svg className='w-6 h-6 text-church-brown' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' />
                      </svg>
                      Fichiers joints
                    </h3>
                    <div className='flex flex-wrap gap-3'>
                      {event.files.map((file, index) => {
                        const fileName = file.split('/').pop() || `Fichier ${index + 1}`;
                        return (
                          <a
                            key={index}
                            href={apiService.getEventFileUrl(event.id, index)}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-white bg-church-brown rounded-lg hover:bg-church-dark transition-all duration-200 shadow-md hover:shadow-lg font-sans'
                          >
                            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                            </svg>
                            {fileName}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className='mt-10 pt-8 border-t border-gray-200'>
                  <button
                    onClick={() => navigate(event.status !== 'published' ? '/admin' : '/')}
                    className='inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-church-brown to-church-dark text-white rounded-lg hover:from-church-dark hover:to-church-brown transition-all duration-200 font-medium shadow-md hover:shadow-lg font-sans'
                  >
                    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' />
                    </svg>
                    {event.status !== 'published' ? 'Retour à l\'admin' : 'Retour aux événements'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
